/**
 * Return-URL после авторизации: вернуть юзера на страницу, с которой он пришёл
 * на вход/регистрацию (а не всегда на главную).
 *
 * Как работает:
 *   1. CTA «Войти/Регистрация» открывает окно входа поверх страницы (hooks/useAuthModal) с адресом
 *      `/login?next=<откуда>` (authUrl); прямой заход на него — полная страница.
 *   2. Login/Register на маунте запоминают next в sessionStorage (rememberReturn)
 *      — чтобы он пережил многошаговость: register → verify → login, и внешний
 *      round-trip Telegram-OAuth (тот же таб).
 *   3. В точке успешного login() (Login / Verify / TelegramCallback) читаем и
 *      очищаем next (consumeReturn) и ведём туда.
 *
 * Безопасность: возвращаемся ТОЛЬКО на внутренний путь ('/...', не '//' и не
 * '/\' — защита от open-redirect) и НЕ на сами auth-страницы (иначе петля).
 */

const KEY = 'auth_return'
const AUTH_RE = /^\/(login|register|forgot-password|verify|verify-pending|confirm-email|auth)(\/|\?|$)/

/** Валидный внутренний путь возврата или '/'. */
export function safeReturnPath(p: string | null | undefined): string {
	if (!p || !/^\/(?![/\\])/.test(p)) return '/'
	if (AUTH_RE.test(p)) return '/'
	return p
}

/** URL входа/регистрации с ?next=<откуда> (для CTA-ссылок и navigate). */
export function authUrl(path: '/login' | '/register', from: string | null | undefined): string {
	const next = safeReturnPath(from)
	return next === '/' ? path : `${path}?next=${encodeURIComponent(next)}`
}

/** Запомнить пункт возврата (переживает register→verify→login и Telegram). */
export function rememberReturn(rawNext: string | null | undefined): void {
	const next = safeReturnPath(rawNext)
	try {
		if (next !== '/') sessionStorage.setItem(KEY, next)
	} catch {
		/* приватный режим / отключённый storage — молча деградируем на '/' */
	}
}

/** Прочитать и очистить пункт возврата; '/' если нет. */
export function consumeReturn(): string {
	try {
		const v = sessionStorage.getItem(KEY)
		if (v) sessionStorage.removeItem(KEY)
		return safeReturnPath(v)
	} catch {
		return '/'
	}
}

/**
 * Путь реферера, если он с нашего origin — глобальный фолбэк для CTA без ?next
 * (напр. hard-nav `<a href="/login">`, который точечно не переведён на authUrl).
 * Реферер same-origin несёт полный путь (Referrer-Policy), cross-origin — только
 * origin → отфильтровывается. Прямой заход по URL реферера не имеет → '/'.
 */
export function referrerPath(): string {
	try {
		if (!document.referrer) return '/'
		const u = new URL(document.referrer)
		if (u.origin !== window.location.origin) return '/'
		return safeReturnPath(u.pathname + u.search)
	} catch {
		return '/'
	}
}
